import { useEffect, useRef, useState } from 'react'
import type { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table'
import {
  columnFiltersToApiFilter,
  sortingToApiSorting,
  paginationToApiPagination,
} from '@/shared/components/data-table/filter-utils'
import type { ListRequest } from '@/shared/lib/api/api-types'

export interface ServerTableState {
  sorting: SortingState
  pagination: PaginationState
  columnFilters: ColumnFiltersState
  onSortingChange: (sorting: SortingState) => void
  onPaginationChange: (pagination: PaginationState) => void
  onColumnFiltersChange: (filters: ColumnFiltersState) => void
  /** Current request payload ready to send to the API */
  request: ListRequest
}

interface UseServerTableOptions {
  defaultPageSize?: number
  defaultSorting?: SortingState
  /**
   * Initial filters applied once when this value first becomes defined.
   * Pass `undefined` while your source is loading to defer hydration.
   */
  initialFilters?: ColumnFiltersState
  /** Same semantics as `initialFilters`, for sorting. */
  initialSorting?: SortingState
  /** Called whenever filters or sorting change (post-hydration). */
  onStateChange?: (state: { columnFilters: ColumnFiltersState; sorting: SortingState }) => void
}

/**
 * Manages server-side DataTable state (sorting, pagination, filters)
 * and exposes a ready-to-use `request` payload for TanStack Query.
 */
export function useServerTable({
  defaultPageSize = 25,
  defaultSorting = [],
  initialFilters,
  initialSorting,
  onStateChange,
}: UseServerTableOptions = {}): ServerTableState {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? defaultSorting)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialFilters ?? [])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  })

  const hydratedRef = useRef({
    filters: initialFilters !== undefined,
    sorting: initialSorting !== undefined,
  })

  // Deferred hydration: apply the first non-undefined initial value we see.
  useEffect(() => {
    if (!hydratedRef.current.filters && initialFilters !== undefined) {
      hydratedRef.current.filters = true
      setColumnFilters(initialFilters)
    }
  }, [initialFilters])

  useEffect(() => {
    if (!hydratedRef.current.sorting && initialSorting !== undefined) {
      hydratedRef.current.sorting = true
      setSorting(initialSorting)
    }
  }, [initialSorting])

  // Reset to first page when filters or sorting change
  function handleSortingChange(next: SortingState) {
    setSorting(next)
    setPagination((p) => ({ ...p, pageIndex: 0 }))
    onStateChange?.({ columnFilters, sorting: next })
  }

  function handleFiltersChange(next: ColumnFiltersState) {
    setColumnFilters(next)
    setPagination((p) => ({ ...p, pageIndex: 0 }))
    onStateChange?.({ columnFilters: next, sorting })
  }

  const request: ListRequest = {
    Filter: columnFiltersToApiFilter(columnFilters),
    Sorting: sortingToApiSorting(sorting),
    Pagination: paginationToApiPagination(pagination),
  }

  return {
    sorting,
    pagination,
    columnFilters,
    onSortingChange: handleSortingChange,
    onPaginationChange: setPagination,
    onColumnFiltersChange: handleFiltersChange,
    request,
  }
}
